import React, { useEffect, useRef, useState } from "react";
import { Bot, Send, Loader2, User as UserIcon, Coins } from "lucide-react";
import { tauroChat } from "../tauroApi";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

export default function TauroChat({ onBuyCredits }) {
  const [messages, setMessages] = useState([
    {
      role: "tauro",
      content:
        "Soy Tauro, tu analista matem\u00e1tico. Preg\u00fantame sobre loter\u00eda dominicana, interpretaci\u00f3n de sue\u00f1os, pal\u00e9s, tripletas o estrategias num\u00e9ricas. \u00bfEn qu\u00e9 puedo ayudarte hoy?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const r = await tauroChat(text, sessionId);
      setSessionId(r.session_id);
      setMessages((m) => [...m, { role: "tauro", content: r.reply }]);
      refreshUser();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Error enviando mensaje";
      if (err?.response?.status === 402) {
        toast({ title: "Sin cr\u00e9ditos", description: detail });
        onBuyCredits && onBuyCredits();
      } else {
        toast({ title: "Error", description: detail });
      }
      setMessages((m) => [...m, { role: "tauro", content: "⚠️ " + detail }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-md border border-blue-100 flex flex-col h-[560px]">
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
            {m.role === "tauro" && (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-red-600 text-white flex items-center justify-center flex-shrink-0 shadow">
                <Bot className="w-4 h-4" />
              </div>
            )}
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
              m.role === "user"
                ? "bg-gradient-to-r from-[#1d6cb1] to-[#2a83ce] text-white rounded-br-sm"
                : "bg-blue-50 text-gray-800 border border-blue-100 rounded-bl-sm"
            }`}>
              {m.content}
            </div>
            {m.role === "user" && (
              <div className="w-9 h-9 rounded-full bg-[#0e4a7a] text-white flex items-center justify-center flex-shrink-0 shadow">
                <UserIcon className="w-4 h-4" />
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-red-600 text-white flex items-center justify-center flex-shrink-0 shadow">
              <Bot className="w-4 h-4" />
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Tauro está analizando...
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="border-t border-blue-100 p-3 bg-blue-50/30">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={loading}
            placeholder="Pregunta a Tauro sobre jugadas, sue\u00f1os, estrategias..."
            className="flex-1 bg-white border-2 border-blue-100 focus:border-[#1d6cb1] rounded-full px-4 py-2.5 text-sm outline-none disabled:opacity-60 transition"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#1d6cb1] to-[#2a83ce] text-white font-semibold shadow hover:shadow-lg disabled:opacity-60 transition"
          >
            <Send className="w-4 h-4" /> Enviar
          </button>
        </div>
        <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500">
          <span>Cada mensaje usa 1 crédito.</span>
          <span className="inline-flex items-center gap-1">
            <Coins className="w-3 h-3" /> {user?.credits ?? 0} disponibles
          </span>
        </div>
      </form>
    </div>
  );
}
